import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IDrawing } from '../drawing-queue/drawing.interface';
import { AuthService } from '../auth.service';
import { environment } from '../../environments/environment';

export interface DrawingsResponse {
  drawings: IDrawing[];
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly baseUrl = environment.api.baseUrl;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    const token = this.authService.token;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  // Public display methods (no auth required)
  public getApprovedDrawings(): Observable<DrawingsResponse> {
    return this.http.get<DrawingsResponse>(`${this.baseUrl}/getApprovedDrawings`, {
      headers: this.getHeaders()
    });
  }

  // Authenticated user methods (use user's JWT token)
  public getPendingDrawings(): Observable<DrawingsResponse> {
    return this.http.get<DrawingsResponse>(`${this.baseUrl}/getPendingDrawings`, {
      headers: this.getHeaders()
    });
  }

  public scanAllDrawings(): Observable<DrawingsResponse> {
    return this.http.get<DrawingsResponse>(`${this.baseUrl}/scanAllDrawings`, {
      headers: this.getHeaders()
    });
  }

  public submitDrawing(drawingData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/submitDrawing`, drawingData, {
      headers: this.getHeaders()
    });
  }

  public approveDrawing(drawing: IDrawing): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/approveDrawing`, drawing, {
      headers: this.getHeaders()
    });
  }

  public denyDrawing(drawing: IDrawing): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/denyDrawing`, drawing, {
      headers: this.getHeaders()
    });
  }

  public updateDrawing(drawing: IDrawing): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/updateDrawing`, drawing, {
      headers: this.getHeaders()
    });
  }

  public deleteDrawing(drawing: IDrawing): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/deleteDrawing`, {
      headers: this.getHeaders(),
      body: drawing
    });
  }

  public clearScreen(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/clearScreen`, {}, {
      headers: this.getHeaders()
    });
  }

  // Auth API calls
  public getUserDetails(): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/userDetails`, {
      headers: this.getHeaders()
    });
  }

  // Utility methods
  public getBaseUrl(): string {
    return this.baseUrl;
  }
}
